import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdDownload, MdLocalShipping } from 'react-icons/md';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import logger from '../utils/logger';

const STATUS_OPTIONS = ['All', 'Pending', 'Shipped', 'Delivered'];
const MOBILE_BREAKPOINT = 700;

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const badgeMeta = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'delivered') {
    return { emoji: '✅', label: 'Delivered', bg: 'rgba(56, 161, 105, 0.2)', color: '#9AE6B4' };
  }
  if (normalized === 'shipped' || normalized === 'processing' || normalized === 'packed') {
    return { emoji: '🚚', label: status, bg: 'rgba(66, 153, 225, 0.2)', color: '#90CDF4' };
  }
  if (normalized === 'cancelled') {
    return { emoji: '❌', label: 'Cancelled', bg: 'rgba(245, 101, 101, 0.2)', color: '#FEB2B2' };
  }
  return { emoji: '⏳', label: status || 'Pending', bg: 'rgba(236, 201, 75, 0.2)', color: '#FAF089' };
};

function OrderHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchOrders = async (status = 'All') => {
    if (!user?.userId) {
      setError('User not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/aastrog_cart/orders', {
        params: {
          status,
          limit: 50,
          offset: 0
        }
      });

      if (response.data?.success) {
        setOrders(response.data.data || []);
      } else {
        setError('Failed to load order history');
      }
    } catch (err) {
      logger.error('❌ fetchOrders error', err);
      setError(err.response?.data?.error || 'Failed to load order history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(statusFilter);
  }, [user?.userId, statusFilter]);

  const handleTrackShipment = (orderId) => {
    navigate(`/orders/${orderId}/track`);
  };

  const handleDownloadInvoice = (orderId) => {
    navigate(`/order-confirmation/${orderId}`);
  };

  const handleOpenOrderDetails = (orderId) => {
    navigate(`/order-confirmation/${orderId}`);
  };

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading-message">⏳ Loading your orders...</div>
      </div>
    );
  }

  return (
    <div className="cart-container order-history-page">
      <div className="cart-header">
        <button className="back-button product-details-back-button" onClick={() => navigate('/home')} aria-label="Go back">
          <MdArrowBack size={isMobile ? 16 : 24} />
          {!isMobile && 'Back'}
        </button>
        <h1 className="cart-title">My Orders</h1>
      </div>

      {error && <div className="error-message">⚠️ {error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt}
            className="continue-shopping-btn"
            onClick={() => setStatusFilter(opt)}
            style={{
              opacity: statusFilter === opt ? 1 : 0.75,
              border: statusFilter === opt ? '1px solid #7f5af0' : '1px solid transparent'
            }}
          >
            {opt}
          </button>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="empty-cart">
          <h3>No orders found</h3>
          <p>Place your first order to see it here.</p>
          <button className="continue-shopping-btn" onClick={() => navigate('/products')}>Browse Products</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {orders.map((order) => {
            const badge = badgeMeta(order.OrderStatus);

            return (
              <div
                key={order.OrderId}
                className="address-card"
                style={{ display: 'block', cursor: 'pointer' }}
                onClick={() => handleOpenOrderDetails(order.OrderId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenOrderDetails(order.OrderId);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <strong>Order #{order.OrderNumber}</strong>
                    <div style={{ fontSize: 13, marginTop: 6 }}>Date: {formatDate(order.CreatedAt)}</div>
                    <div style={{ fontSize: 13 }}>Total: {formatCurrency(order.TotalAmount)}</div>
                    <div style={{ fontSize: 13 }}>Items: {order.ItemCount || 0}</div>
                  </div>
                  <div
                    style={{
                      alignSelf: 'flex-start',
                      padding: '6px 10px',
                      borderRadius: 999,
                      background: badge.bg,
                      color: badge.color,
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    {badge.emoji} {badge.label}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'auto auto',
                    gap: 8,
                    alignItems: 'stretch'
                  }}
                >
                  <button
                    className="continue-btn"
                    aria-label="Download Invoice"
                    title="Download Invoice"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadInvoice(order.OrderId);
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}
                  >
                    {isMobile ? <MdDownload size={20} /> : 'Download Invoice'}
                  </button>
                  <button
                    className="continue-btn"
                    aria-label="Order Tracking"
                    title="Order Tracking"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrackShipment(order.OrderId);
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}
                  >
                    {isMobile ? <MdLocalShipping size={20} /> : 'Order Tracking'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OrderHistory;